/**
 * First-login legal acknowledgement line for invited members.
 *
 * Invited trainers and learners never see the self-service signup
 * questionnaire, so they never tick the acceptance checkbox — T&C §1.3 still
 * requires them to be shown the documents they are bound by. This is that
 * showing: a single informational line plus a "Got it" button that records an
 * append-only `member_ack` proof server-side.
 *
 * Deliberately NOT a blocking modal — the member's own acceptance is not what
 * makes the contract (their organization's admin already accepted on their
 * behalf), so gating the app on it would be a false gate. It rides above the
 * page content until acknowledged, and stays put if the call fails.
 */
import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { DURATION_NORMAL, EASE_OUT } from '@/lib/motion';
import { LEGAL_DOCS } from '@/config/legal';
import { useAuth } from '../../hooks/useAuth';

/** Same link treatment as the cookie consent banner (the other legal surface). */
const LEGAL_LINK_CLASS =
  'underline underline-offset-2 transition-colors duration-fast ' +
  'hover:text-primary-600 dark:hover:text-primary-300 focus-ring rounded-sm';

const ERROR_ID = 'legal-ack-error';

const LegalAcknowledgementNotice: React.FC = () => {
  const { legalAckRequired, acknowledgeLegal } = useAuth();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const reduceMotion = useReducedMotion();

  const handleAcknowledge = async () => {
    setSaving(true);
    setFailed(false);
    try {
      await acknowledgeLegal();
    } catch {
      // The acknowledgement is legal proof: never pretend it landed. Keep the
      // notice up, re-enable the button and say so.
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {legalAckRequired && (
        <motion.section
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
          transition={{ duration: DURATION_NORMAL, ease: EASE_OUT }}
          aria-label="Legal acknowledgement"
          className="mx-auto mb-4 w-full max-w-7xl rounded-xl border border-border-primary bg-surface-elevated px-4 py-3"
        >
          {/* Stacks on a phone (button drops below the sentence), one row from sm up. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-text-primary">
              By continuing you acknowledge the{' '}
              <a
                href={LEGAL_DOCS.termsAndConditions}
                target="_blank"
                rel="noreferrer noopener"
                className={LEGAL_LINK_CLASS}
              >
                Terms &amp; Conditions
              </a>{' '}
              and the{' '}
              <a
                href={LEGAL_DOCS.privacyPolicy}
                target="_blank"
                rel="noreferrer noopener"
                className={LEGAL_LINK_CLASS}
              >
                Privacy Policy
              </a>
              .
            </p>
            <button
              type="button"
              onClick={handleAcknowledge}
              disabled={saving}
              aria-busy={saving}
              aria-describedby={failed ? ERROR_ID : undefined}
              className="btn btn-primary btn-sm shrink-0 self-start sm:self-auto"
            >
              Got it
            </button>
          </div>

          {failed && (
            <p
              id={ERROR_ID}
              role="alert"
              className="mt-2 text-xs font-medium text-error-600 dark:text-error-400"
            >
              We could not record that just now. Please try again.
            </p>
          )}
        </motion.section>
      )}
    </AnimatePresence>
  );
};

export default LegalAcknowledgementNotice;
